import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Container, Spinner, Alert } from 'react-bootstrap';
import { useLanguage } from '../context/LanguageContext';
import {
  generateInvoicePreview,
  downloadPDF,
  revokeBlobUrl,
  PDFPreviewResult
} from '../utils/pdfHelper';

interface InvoiceItem {
  id: number;
  engagementCode: string;
  companyName: string;
  brNumber: string;
  yearEndDate: string;
  invoice: string;
  auditReportDate: string;
  opinion: string;
  typeOfEngagement: string;
}

const Invoice: React.FC = () => {
  // Sample invoice data combining Client List and Engagement data with updated invoice values
  const [invoiceItems, setInvoiceItems] = useState<InvoiceItem[]>([
    {
      id: 1,
      engagementCode: 'AUD20260001',
      companyName: 'ABC Company Limited',
      brNumber: '12345678',
      yearEndDate: '2025-12-31',
      invoice: '8000',
      auditReportDate: '2026-01-15',
      opinion: 'Disclaimer',
      typeOfEngagement: 'statutory audit'
    },
    {
      id: 2,
      engagementCode: 'ACT20260001',
      companyName: 'XYZ Corporation',
      brNumber: '87654321',
      yearEndDate: '2025-12-31',
      invoice: '12000',
      auditReportDate: '2026-01-15',
      opinion: 'Disclaimer',
      typeOfEngagement: 'consulting'
    },
    {
      id: 3,
      engagementCode: 'GCS20260001',
      companyName: 'DEF Industries',
      brNumber: '11223344',
      yearEndDate: '2025-12-31',
      invoice: '15000',
      auditReportDate: '2026-01-15',
      opinion: 'Qualified',
      typeOfEngagement: 'consulting'
    }
  ]);

  // State for selected invoice and preview modal
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceItem | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  
  // PDF预览相关状态
  const [pdfPreview, setPdfPreview] = useState<PDFPreviewResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 清理Blob URL
  useEffect(() => {
    return () => {
      if (pdfPreview) {
        revokeBlobUrl(pdfPreview.blobUrl);
      }
    };
  }, [pdfPreview]);

  // 关闭预览时清理资源
  const handleClosePreview = () => {
    if (pdfPreview) {
      revokeBlobUrl(pdfPreview.blobUrl);
      setPdfPreview(null);
    }
    setShowPreview(false);
    setSelectedInvoice(null);
    setError(null);
  };

  // Handle invoice selection
  const handleSelectInvoice = async (invoice: InvoiceItem) => {
    setSelectedInvoice(invoice);
    setShowPreview(true);
    setIsLoading(true);
    setError(null);

    try {
      // 生成PDF预览
      const previewResult = await generateInvoicePreview(invoice);
      setPdfPreview(previewResult);
    } catch (err) {
      console.error('生成PDFpreview失败:', err);
      setError('生成PDFpreview失败，請稍後重試');
    } finally {
      setIsLoading(false);
    }
  };

  // 导出PDF文件
  const handleExportPDF = () => {
    if (!selectedInvoice || !pdfPreview) return;

    try {
      const fileName = `invoice-${selectedInvoice.engagementCode}-${selectedInvoice.invoice}.pdf`;
      downloadPDF(pdfPreview.pdfBytes, fileName);
    } catch (err) {
      console.error('Export PDF失败:', err);
      setError('Export PDF失败，請稍後重試');
    }
  };

  const { t } = useLanguage();

  return (
    <div className="invoice 發票">
      <Container>
        <h2>{t('invoice 發票')}</h2>
        <h3>{t('invoiceList')}</h3>

        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>
                <div>#</div>
                <div className="text-muted small">(編號)</div>
              </th>
              <th>
                <div>{t('Engagement Code')}</div>
                <div className="text-muted small">(項目代碼)</div>
              </th>
              <th>
                <div>{t('companyName')}</div>
                <div className="text-muted small">(公司名稱)</div>
              </th>
              <th>
                <div>{t('brNumber')}</div>
                <div className="text-muted small">(商業登記號碼)</div>
              </th>
              <th>
                <div>Year end date</div>
                <div className="text-muted small">(年度結算日期)</div>
              </th>
              <th>
                <div>{t('invoicei')}</div>
                <div className="text-muted small">(發票)</div>
              </th>
              <th>
                <div>Audit Report Date</div>
                <div className="text-muted small">(審計報告日期)</div>
              </th>
              <th>
                <div>Opinion</div>
                <div className="text-muted small">(意見)</div>
              </th>
              <th>
                <div>Type of engagement</div>
                <div className="text-muted small">(項目類型)</div>
              </th>
              <th>
                <div>Action</div>
                <div className="text-muted small">(操作)</div>
              </th>
            </tr>
          </thead>
          <tbody>
            {invoiceItems.map((item) => (
              <tr key={item.id}>
                <td>{item.id}</td>
                <td>{item.engagementCode}</td>
                <td>{item.companyName}</td>
                <td>{item.brNumber}</td>
                <td>{item.yearEndDate}</td>
                <td>{item.invoice}</td>
                <td>{item.auditReportDate}</td>
                <td>{item.opinion}</td>
                <td>{item.typeOfEngagement}</td>
                <td>
                  <Button
                    variant="primary"
                    onClick={() => handleSelectInvoice(item)}
                  >
                    {t('preview')} & {t('print')}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Container>

      {/* Preview Modal */}
      <Modal 
        show={showPreview} 
        onHide={handleClosePreview} 
        size="xl"
        dialogClassName="pdf-preview-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {t('invoice')} {t('preview')} - {selectedInvoice?.companyName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ minHeight: '600px' }}>
          {isLoading && (
            <div className="text-center py-5">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">加载中...</span>
              </Spinner>
              <p className="mt-3">正在生成PDF preview...</p>
            </div>
          )}
          
          {error && (
            <Alert variant="danger">
              {error}
            </Alert>
          )}
          
          {!isLoading && !error && pdfPreview && (
            <div className="pdf-preview-container">
              <iframe
                src={pdfPreview.blobUrl}
                width="100%"
                height="700px"
                style={{ border: '1px solid #dee2e6', borderRadius: '4px' }}
                title="PDF Preview"
              />
            </div>
          )}
          
          {!isLoading && !error && !pdfPreview && selectedInvoice && (
            <Alert variant="warning">
              PDF preview生成失败
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClosePreview}>
            {t('close')}
          </Button>
          <Button 
            variant="primary" 
            onClick={handleExportPDF}
            disabled={!pdfPreview || isLoading}
          >
            {t('print')} {t('invoice')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Invoice;